<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';

const props = defineProps({
  error: { type: String, required: true },
});

const emit = defineEmits(['retry']);

const { orientation, status, createdAt } = useMessageContext();

const { t } = useI18n();

const canRetry = computed(() => !hasOneDayPassed(createdAt.value));

// WhatsApp Cloud API error code translations (pt-BR)
// Maps known numeric codes to user-friendly messages
const whatsappErrorMap = {
  130429:
    'Limite de mensagens excedido. Aguarde alguns minutos e tente novamente. (Código: 130429)',
  131000:
    'Erro desconhecido ao enviar a mensagem. Tente novamente mais tarde. (Código: 131000)',
  131005:
    'Permissão negada. Verifique se o número do WhatsApp Business está corretamente configurado na Meta. (Código: 131005)',
  131008:
    'O parâmetro enviado é inválido ou está faltando. Verifique o template e tente novamente. (Código: 131008)',
  131009:
    'O tipo de parâmetro do template está incorreto. Verifique os campos e tente novamente. (Código: 131009)',
  131016:
    'O serviço do WhatsApp está temporariamente indisponível. Tente novamente em alguns minutos. (Código: 131016)',
  131021:
    'O destinatário não possui uma conta válida no WhatsApp ou o número está incorreto. (Código: 131021)',
  131026:
    'A mensagem não pôde ser entregue. O destinatário pode ter bloqueado este número ou o número é inválido. (Código: 131026)',
  131042:
    'Sua conta do WhatsApp está com um problema relacionado ao faturamento. Verifique sua conta do WhatsApp na Meta ou contate o suporte. (Código: 131042)',
  131045:
    'O número do destinatário ainda não aceitou os novos termos de serviço do WhatsApp. (Código: 131045)',
  131047:
    'Você excedeu o limite de mensagens permitidos nas últimas 24 horas. Aguarde e tente novamente. (Código: 131047)',
  131048:
    'Este template de mensagem não foi aprovado ou ainda está pendente de aprovação pela Meta. (Código: 131048)',
  131051:
    'O tipo de mensagem não é suportado neste momento. (Código: 131051)',
  131052:
    'O download da mídia falhou. Verifique se o arquivo é válido e acessível. (Código: 131052)',
  131053:
    'O upload da mídia falhou. Verifique o arquivo e tente novamente. (Código: 131053)',
  131056:
    'Número de parâmetros no template não corresponde ao esperado. Verifique os campos variáveis. (Código: 131056)',
  131057:
    'A conta não tem permissão para enviar este tipo de mensagem experimental. (Código: 131057)',
  132000:
    'O número de parâmetros do template não corresponde ao esperado. Revise o template. (Código: 132000)',
  132001:
    'O template não existe ou foi excluído. Sincronize seus templates. (Código: 132001)',
  132005:
    'O texto do template traduzido contém muitos caracteres. (Código: 132005)',
  132007:
    'A política do template foi violada. Verifique o conteúdo do template na Meta. (Código: 132007)',
  132012:
    'O parâmetro do template está com o formato incorreto. (Código: 132012)',
  132015:
    'O template está pausado pela Meta. Verifique o status na sua conta Business. (Código: 132015)',
  132016:
    'O template está desabilitado pela Meta. Verifique o status na sua conta Business. (Código: 132016)',
  133000:
    'Erro na comunicação com o servidor do WhatsApp. Tente novamente mais tarde. (Código: 133000)',
  133004:
    'O servidor do WhatsApp está temporariamente indisponível. Tente novamente em alguns minutos. (Código: 133004)',
  133010:
    'O número do remetente não está registrado. Verifique as configurações da conta na Meta. (Código: 133010)',
  135000:
    'Falha genérica da API do WhatsApp. Tente novamente ou contate o suporte. (Código: 135000)',
  131049:
    'Esta mensagem não foi entregue porque o destinatário atingiu o limite de mensagens de marketing do WhatsApp. Aguarde o contato responder ou tente novamente em 24 horas. (Código: 131049)',
  80007:
    'Limite de taxa atingido. Aguarde alguns minutos antes de enviar novas mensagens. (Código: 80007)',
};

// Extract WhatsApp error code from the raw error string and return a friendly message
const translatedError = computed(() => {
  if (!props.error) return '';
  // Try to extract a numeric code from the start of the error string (e.g., "131042: ...")
  const match = props.error.match(/^(\d{4,6})\s*[:-]/);
  if (match) {
    const code = parseInt(match[1], 10);
    if (whatsappErrorMap[code]) {
      return whatsappErrorMap[code];
    }
  }
  // Fallback: return the raw error as-is
  return props.error;
});
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <span>{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
    <div class="relative group">
      <div
        class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      >
        <Icon
          icon="i-lucide-alert-triangle"
          class="text-n-ruby-11 size-[14px]"
        />
      </div>
      <div
        class="absolute bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-64 text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all break-words leading-relaxed"
        :class="{
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ translatedError }}
      </div>
    </div>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>

